import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageHero } from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { usePageTitle } from '../hooks/usePageTitle';
import { FOUNDATION, ASSESSMENT_URL } from '../config/foundation';

export function Apply() {
  usePageTitle('Apply for Support');
  const applyMailto = `mailto:${FOUNDATION.email}?subject=${encodeURIComponent('Programme application enquiry')}`;

  const steps = [
    { num: '1', title: 'Take the assessment', desc: 'Complete the free 41-question Readiness Assessment to discover your profile and the right starting point.' },
    { num: '2', title: 'Submit your application', desc: "Tell us a little about your situation and what you're hoping to achieve. It only takes a few minutes." },
    { num: '3', title: 'We match you', desc: 'We confirm eligibility, allocate a funded place where available, and pair you with a coach or mentor.' },
    { num: '4', title: 'Begin your programme', desc: 'Get access to your portal, resources, and sessions — and start building momentum.' },
  ];

  const eligibility = [
    'Aged 18 or over and based in England, Wales or Scotland',
    'Ready to commit around one hour per week for the duration of the programme',
    'Open to coaching, honest reflection, and practical action between sessions',
    'Seeking to up-skill, change direction, or move toward self-sufficiency',
  ];

  return (
    <Layout>
      <PageHero
        eyebrow="Apply for Support"
        title={<>Start your <span className="text-mustard-gold">journey</span></>}
      >
        Our programmes are delivered as part of our charitable work &mdash; with free and funded
        places for those who need them. Here's how to apply.
      </PageHero>

      {/* Funding note */}
      <section className="py-12 sm:py-16">
        <div className="section">
          <Reveal className="max-w-3xl mx-auto">
            <div className="card p-6 sm:p-8 flex flex-col sm:flex-row gap-5 items-start">
              <div className="w-11 h-11 rounded-xl bg-mustard-green-dark/5 text-mustard-green-dark flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <div>
                <h3 className="font-display font-bold text-mustard-green-dark mb-1">Free &amp; funded places</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  As a registered charity, we offer programmes free of charge to eligible participants,
                  funded by donations and grants. The free Readiness Assessment and CV check are open to
                  everyone. Where demand exceeds available places, we prioritise those facing the
                  greatest barriers.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How it works */}
      <section className="pb-4 sm:pb-8">
        <div className="section">
          <Reveal className="text-center mb-12">
            <p className="eyebrow mb-3">The Process</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-display">
              <span className="text-gradient">How to apply in four steps</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((s, i) => (
              <Reveal key={s.num} delay={i * 100}>
                <StepCard {...s} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <Reveal>
              <p className="eyebrow mb-3">Eligibility</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-mustard-green-dark tracking-display mb-5">
                Who can apply
              </h2>
              <p className="text-slate-500 leading-relaxed mb-4">
                We welcome applications from people across England, Wales and Scotland who are ready
                to invest in their own development and want to build skills, confidence, and a
                sustainable livelihood.
              </p>
              <p className="text-slate-500 leading-relaxed">
                You don't need any particular qualifications &mdash; just a willingness to engage with
                the programme. If you're not sure whether it's right for you, the Readiness Assessment
                will help, and our team is always happy to talk it through.
              </p>
            </Reveal>
            <Reveal delay={120}>
              <ul className="space-y-3">
                {eligibility.map((item) => (
                  <li key={item} className="flex items-start gap-3 p-4 rounded-xl border border-slate-100 bg-white">
                    <span className="w-5 h-5 rounded-full bg-mustard-green-dark/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-mustard-green-dark" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    </span>
                    <span className="text-sm text-mustard-charcoal font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Apply CTA — inline band keeps the sign-in note below the buttons */}
      <section className="pb-16 sm:pb-24 pt-4">
        <div className="section">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-brand-gradient shadow-glow-green text-center p-10 sm:p-16">
              <div className="absolute inset-0 noise opacity-[0.07] mix-blend-overlay" aria-hidden="true" />
              <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-[30rem] h-[30rem] rounded-full bg-mustard-gold/15 blur-3xl animate-glow-pulse" aria-hidden="true" />
              <div className="relative">
                <h2 className="font-display text-2xl sm:text-4xl font-bold text-white mb-4 tracking-display">
                  Ready to apply?
                </h2>
                <p className="text-white/70 mb-8 max-w-lg mx-auto">
                  Start with the free Readiness Assessment, then send us your application. We'll be in
                  touch about the next available place.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a href={ASSESSMENT_URL} className="btn-gold text-center">Take the Assessment</a>
                  <a href={applyMailto} className="glass px-6 py-3 text-white rounded-xl font-semibold text-sm hover:bg-white/20 transition-all text-center">
                    Start your application
                  </a>
                </div>
                <p className="text-white/50 text-xs mt-6">
                  Already have an account?{' '}
                  <Link to="/login" className="text-white/80 hover:text-white underline">Sign in to your portal</Link>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}

function StepCard({ num, title, desc }: { num: string; title: string; desc: string }) {
  return (
    <div className="card h-full p-6">
      <div className="w-11 h-11 rounded-2xl bg-brand-gradient text-mustard-gold-light flex items-center justify-center mb-4 font-display text-lg font-bold shadow-lg shadow-mustard-green-dark/15">
        {num}
      </div>
      <h3 className="font-display font-bold text-mustard-green-dark mb-1.5 text-sm">{title}</h3>
      <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
    </div>
  );
}
